import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  ChevronRight, ChevronLeft, CheckCircle, XCircle, Eye,
  RotateCcw, BarChart3, Clock, ArrowLeft, Zap, BookOpen, Filter, Home, Languages
} from "lucide-react";
import PromoSidebar from "../components/PromoSidebar";
import PremiumPopup from "../components/PremiumPopup";

const API = window.location.origin;
const FREE_TOPIC_LIMIT = 2;

// ── Language Bar ──────────────────────────────────────────────────
function LanguageBar({ languages, selectedLang, onSelect, loading }) {
  const allLangs = [{ code: "original", name: "Original", native: "A" }, ...languages];
  return (
    <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
      <Languages className="w-4 h-4 text-slate-500 shrink-0" />
      <div className="flex gap-1.5 flex-nowrap">
        {allLangs.map(lang => (
          <button
            key={lang.code}
            data-testid={`lang-btn-${lang.code}`}
            onClick={() => onSelect(lang)}
            title={lang.name}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold border transition-all duration-150 ${
              selectedLang.code === lang.code
                ? "bg-indigo-600 border-indigo-500 text-white"
                : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20"
            }`}
          >
            {lang.code === "original" ? "Original" : lang.native}
            {loading === lang.code && <span className="ml-1 opacity-60">...</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Translation hook ──────────────────────────────────────────────
function useTranslations() {
  const [selectedLang, setSelectedLang] = useState({ code: "original", name: "Original", native: "Original" });
  const [languages, setLanguages] = useState([]);
  const [cache, setCache] = useState({});   // `${qid}_${langCode}` → {question_text, options, answer, explanation}
  const [translating, setTranslating] = useState(null); // lang code being loaded

  useEffect(() => {
    fetch(`${API}/api/question-bank/languages`)
      .then(r => r.json())
      .then(d => setLanguages(d.languages || []))
      .catch(() => {});
  }, []);

  const translate = useCallback(async (questions, lang) => {
    if (lang.code === "original") return;
    setTranslating(lang.code);
    const pending = questions.filter(q => q.id && !cache[`${q.id}_${lang.code}`]);
    await Promise.all(pending.map(async q => {
      const key = `${q.id}_${lang.code}`;
      try {
        const r = await fetch(`${API}/api/question-bank/translate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question_id: q.id, lang_code: lang.code, lang_name: lang.name })
        });
        const d = await r.json();
        if (d.success) {
          setCache(prev => ({ ...prev, [key]: d }));
        }
      } catch (_) {}
    }));
    setTranslating(null);
  }, [cache]);

  const getDisplay = useCallback((q) => {
    if (selectedLang.code === "original" || !q.id) return q;
    const tr = cache[`${q.id}_${selectedLang.code}`];
    if (!tr) return q;
    return { ...q, question_text: tr.question_text, options: tr.options,
             correct_answer: tr.answer || q.correct_answer, explanation: tr.explanation };
  }, [selectedLang, cache]);

  return { selectedLang, setSelectedLang, languages, translate, getDisplay, translating };
}

// ── Quiz Engine ───────────────────────────────────────────────────
function QuizMode({ questions, onExit, trans }) {
  const { selectedLang, setSelectedLang, languages, translate, getDisplay, translating } = trans;
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const q = questions[current];
  const dq = getDisplay(q);
  const progress = ((current + 1) / questions.length) * 100;

  // Translate current + next question eagerly
  useEffect(() => {
    if (selectedLang.code === "original") return;
    const toTranslate = questions.slice(current, current + 2);
    translate(toTranslate, selectedLang);
  }, [current, selectedLang]); // eslint-disable-line

  // Timer
  useEffect(() => {
    if (finished) return;
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [finished]);

  const handleSelect = (key) => {
    if (revealed) return;
    setSelected(key);
    setRevealed(true);
    setAnswers(prev => [...prev, { q: current, selected: key, correct: q.correct_answer }]);
  };

  const handleNext = () => {
    if (current + 1 >= questions.length) {
      setFinished(true);
    } else {
      setCurrent(c => c + 1);
      setSelected(null);
      setRevealed(false);
    }
  };

  const handleRestart = () => {
    setCurrent(0); setSelected(null); setRevealed(false);
    setAnswers([]); setFinished(false); setSeconds(0);
  };

  const score = answers.filter(a => a.selected === a.correct).length;

  // ── Result Screen ─────────────────────────────────────────────
  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    const grade = pct >= 80 ? "Excellent!" : pct >= 60 ? "Good Job!" : pct >= 40 ? "Keep Practicing" : "Needs Improvement";
    const gradeColor = pct >= 80 ? "text-emerald-400" : pct >= 60 ? "text-blue-400" : pct >= 40 ? "text-amber-400" : "text-red-400";
    const mins = Math.floor(seconds / 60), secs = seconds % 60;

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-12">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="text-6xl font-black mb-2">
            <span className={gradeColor}>{pct}%</span>
          </div>
          <p className={`text-xl font-bold mb-1 ${gradeColor}`}>{grade}</p>
          <p className="text-slate-400 text-sm mb-6">
            {score} / {questions.length} correct · {mins}m {secs}s
          </p>

          {/* Score bar */}
          <div className="w-full bg-white/10 rounded-full h-3 mb-6">
            <div
              className={`h-3 rounded-full transition-all duration-1000 ${pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-blue-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>

          {/* Per-question review */}
          <div className="text-left space-y-2 mb-6 max-h-60 overflow-y-auto pr-1">
            {answers.map((a, i) => {
              const q = questions[a.q];
              const isRight = a.selected === a.correct;
              return (
                <div key={i} className={`text-xs p-2.5 rounded-lg border ${isRight ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`}>
                  <p className="text-slate-300 mb-1 line-clamp-1">{i + 1}. {q.question_text.substring(0, 70)}...</p>
                  <div className="flex gap-2">
                    {!isRight && <span className="text-red-400">You: {a.selected}. {q.options[a.selected]?.substring(0, 30)}</span>}
                    <span className={isRight ? "text-emerald-400" : "text-emerald-400/60"}>✓ {a.correct}. {q.options[a.correct]?.substring(0, 30)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button onClick={handleRestart} className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2.5 rounded-xl transition-colors">
              <RotateCcw className="w-4 h-4" /> Retry
            </button>
            <button onClick={onExit} className="flex-1 bg-white/10 hover:bg-white/20 text-white font-semibold py-2.5 rounded-xl transition-colors">
              Exit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Question Screen ───────────────────────────────────────────
  const optStyle = (key) => {
    if (!revealed) return "bg-white/5 hover:bg-white/10 border-white/10 hover:border-indigo-500/40 cursor-pointer text-slate-300";
    if (key === q.correct_answer) return "bg-emerald-500/20 border-emerald-500/50 text-emerald-300";
    if (key === selected && key !== q.correct_answer) return "bg-red-500/20 border-red-500/50 text-red-300";
    return "bg-white/5 border-white/10 text-slate-500";
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Language bar */}
      <LanguageBar languages={languages} selectedLang={selectedLang}
        onSelect={lang => { setSelectedLang(lang); translate([q], lang); }}
        loading={translating} />

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-slate-500 mb-1.5">
          <span>Question {current + 1} of {questions.length}</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
          </span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full">
          <div className="h-1.5 bg-indigo-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {(q.year || q.paper_type === "pyp" || q.paper_label) && (
          <span className="text-xs px-2.5 py-0.5 rounded-full border font-bold bg-orange-500/20 border-orange-500/40 text-orange-400 tracking-wide">
            PYQ{q.year ? `: ${q.year}` : ""}
          </span>
        )}
        {q.paper_label && q.paper_label !== `PYQ ${q.year}` && (
          <span className="text-xs px-2.5 py-0.5 rounded-full border font-bold bg-amber-500/20 border-amber-500/40 text-amber-300">
            {q.paper_label}
          </span>
        )}
        {q.difficulty && (
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize
            ${q.difficulty === "easy" ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400"
              : q.difficulty === "hard" ? "bg-red-500/20 border-red-500/30 text-red-400"
              : "bg-amber-500/20 border-amber-500/30 text-amber-400"}`}>
            {q.difficulty}
          </span>
        )}
        {!q.paper_label && q.year && (
          <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-700/50 border-white/10 text-slate-400">
            {q.year}
          </span>
        )}
      </div>

      {/* Question */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-4">
        <p className="text-white text-base leading-relaxed font-medium">{dq.question_text}</p>
      </div>

      {/* Options */}
      <div className="space-y-2.5 mb-5">
        {Object.entries(dq.options || {}).map(([key, val]) => (
          <button
            key={key}
            onClick={() => handleSelect(key)}
            disabled={revealed}
            className={`w-full flex items-center gap-3 border rounded-xl px-4 py-3 text-sm text-left transition-all duration-150 ${optStyle(key)}`}
          >
            <span className="w-6 h-6 flex items-center justify-center rounded-full border border-current text-xs font-bold shrink-0">{key}</span>
            <span className="flex-1">{val}</span>
            {revealed && key === q.correct_answer && <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />}
            {revealed && key === selected && key !== q.correct_answer && <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
          </button>
        ))}
      </div>

      {/* Explanation */}
      {revealed && dq.explanation && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 mb-4">
          <p className="text-xs font-semibold text-blue-400 mb-1">Explanation</p>
          <p className="text-slate-300 text-sm leading-relaxed">{dq.explanation}</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        {!revealed ? (
          <button onClick={() => { setRevealed(true); setAnswers(prev => [...prev, { q: current, selected: null, correct: q.correct_answer }]); }}
            className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1">
            <Eye className="w-4 h-4" /> Skip
          </button>
        ) : <div />}
        <button
          onClick={handleNext}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"
        >
          {current + 1 >= questions.length ? "See Results" : "Next"} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ── Practice Mode ────────────────────────────────────────────────
function PracticeMode({ questions, trans }) {
  const { selectedLang, setSelectedLang, languages, translate, getDisplay, translating } = trans;
  const [revealed, setRevealed] = useState({});
  const [selected, setSelected] = useState({});
  const [showAll, setShowAll] = useState(false);

  // Translate all visible questions when language changes
  useEffect(() => {
    if (selectedLang.code !== "original") {
      translate(questions, selectedLang);
    }
  }, [selectedLang]); // eslint-disable-line

  const handleSelect = (qi, key) => {
    if (revealed[qi]) return;
    setSelected(p => ({ ...p, [qi]: key }));
    setRevealed(p => ({ ...p, [qi]: true }));
  };

  const optStyle = (qi, key, correct) => {
    const isRevealed = revealed[qi] || showAll;
    const sel = selected[qi];
    if (!isRevealed) return "bg-white/5 hover:bg-white/10 border-white/10 cursor-pointer text-slate-300";
    if (key === correct) return "bg-emerald-500/20 border-emerald-500/50 text-emerald-300";
    if (key === sel && key !== correct) return "bg-red-500/20 border-red-500/50 text-red-300";
    return "bg-white/5 border-white/10 text-slate-500";
  };

  return (
    <div>
      {/* Language bar */}
      <LanguageBar languages={languages} selectedLang={selectedLang}
        onSelect={lang => setSelectedLang(lang)} loading={translating} />

      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowAll(s => !s)}
          className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1.5 border border-indigo-500/30 rounded-lg px-3 py-1.5 transition-all"
        >
          <Eye className="w-3.5 h-3.5" /> {showAll ? "Hide Answers" : "Show All Answers"}
        </button>
      </div>
      <div className="space-y-5">
        {questions.map((q, qi) => {
          const dq = getDisplay(q);
          return (
          <div key={qi} className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <div className="flex gap-3 mb-3">
              <span className="shrink-0 w-7 h-7 bg-indigo-500/20 text-indigo-400 rounded-full flex items-center justify-center text-xs font-bold">{qi + 1}</span>
              <div className="flex-1">
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {(q.year || q.paper_type === "pyp" || q.paper_label) && (
                    <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-400 font-bold tracking-wide">
                      PYQ{q.year ? `: ${q.year}` : ""}
                    </span>
                  )}
                  {q.paper_label && q.paper_label !== `PYQ ${q.year}` && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 font-bold">
                      {q.paper_label}
                    </span>
                  )}
                  {q.difficulty && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium capitalize
                      ${q.difficulty === "easy" ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-400"
                        : q.difficulty === "hard" ? "bg-red-500/20 border-red-500/30 text-red-400"
                        : "bg-amber-500/20 border-amber-500/30 text-amber-400"}`}>
                      {q.difficulty}
                    </span>
                  )}
                </div>
                <p className="text-slate-100 text-sm font-medium leading-relaxed">{dq.question_text}</p>
              </div>
            </div>
            <div className="space-y-2 ml-10">
              {Object.entries(dq.options || {}).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => handleSelect(qi, key)}
                  className={`w-full flex items-center gap-2.5 border rounded-lg px-3 py-2 text-sm text-left transition-all ${optStyle(qi, key, q.correct_answer)}`}
                >
                  <span className="font-bold text-xs shrink-0">{key}.</span>
                  <span className="flex-1">{val}</span>
                  {(revealed[qi] || showAll) && key === q.correct_answer && <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />}
                </button>
              ))}
            </div>
            {(revealed[qi] || showAll) && dq.explanation && (
              <div className="mt-3 ml-10 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-400 mb-0.5">Answer: {q.correct_answer}. {q.correct_answer_text}</p>
                <p className="text-slate-400 text-xs leading-relaxed">{dq.explanation}</p>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Topic Page ──────────────────────────────────────────────
export default function QuestionBankTopic() {
  const { examType, subject, topicSlug } = useParams();
  const trans = useTranslations();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // null=landing, "quiz", "practice"
  const [page, setPage] = useState(1);
  const [diffFilter, setDiffFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("All");
  const [allQuestions, setAllQuestions] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);

  // Read user from localStorage (this page is public, no user prop)
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);
  const isPaidUser = currentUser && currentUser.plan && currentUser.plan !== 'free';

  // Track visited topics for free users and enforce 2-topic limit
  useEffect(() => {
    if (!currentUser || isPaidUser) return; // paid users have no limit
    const topicKey = `${examType}/${subject}/${topicSlug}`;
    const stored = localStorage.getItem('qb_visited_topics');
    const visited = stored ? JSON.parse(stored) : [];
    if (visited.includes(topicKey)) return; // already counted
    if (visited.length >= FREE_TOPIC_LIMIT) {
      setShowPaywall(true);
    } else {
      localStorage.setItem('qb_visited_topics', JSON.stringify([...visited, topicKey]));
    }
  }, [examType, subject, topicSlug, currentUser, isPaidUser]);

  const fetchPage = useCallback((p, diff = diffFilter) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 50 });
    if (diff) params.set("difficulty", diff);
    axios.get(`${API}/api/question-bank/public/${examType}/${subject}/${topicSlug}?${params}`)
      .then(r => {
        setData(r.data);
        setAllQuestions(r.data.questions || []);
        setPage(p);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [examType, subject, topicSlug, diffFilter]);

  useEffect(() => { fetchPage(1); setSectionFilter("All"); }, [examType, subject, topicSlug]);

  // Derive unique sections from loaded questions
  const availableSections = useMemo(() => {
    const sections = new Set();
    allQuestions.forEach(q => { if (q.section) sections.add(q.section); });
    return sections.size > 1 ? ["All", ...Array.from(sections).sort()] : [];
  }, [allQuestions]);

  // Filtered questions for quiz/practice/preview
  const filteredQuestions = useMemo(() => {
    if (!sectionFilter || sectionFilter === "All") return allQuestions;
    return allQuestions.filter(q => q.section === sectionFilter);
  }, [allQuestions, sectionFilter]);

  const sectionCountMap = useMemo(() => {
    const map = { All: allQuestions.length };
    allQuestions.forEach(q => {
      if (q.section) map[q.section] = (map[q.section] || 0) + 1;
    });
    return map;
  }, [allQuestions]);

  const topicName = data?.topic_name || topicSlug?.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const examLabel = data?.exam_label || examType?.toUpperCase();
  const subjectLabel = data?.subject_label || subject?.replace(/_/g, " ");
  const totalQ = data?.total || 0;

  const pageTitle = `${topicName} MCQ Questions for ${examLabel} [${totalQ} Q Free] - Descriptive Test`;
  const metaDesc = `Practice ${totalQ} ${topicName} MCQ questions for ${examLabel} exam. AI-powered explanations. Subject: ${subjectLabel}. Free on Descriptive Test.`;

  const faqSchema = allQuestions.slice(0, 5).map(q => ({
    "@type": "Question",
    name: q.question_text.substring(0, 200),
    acceptedAnswer: {
      "@type": "Answer",
      text: `${q.correct_answer}. ${q.correct_answer_text}${q.explanation ? " — " + q.explanation : ""}`
    }
  }));

  if (mode === "quiz" && filteredQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-[#0a0d14] text-white px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-lg font-bold text-white">{topicName} Quiz</h2>
              <p className="text-xs text-slate-500">
                {filteredQuestions.length} questions · {examLabel}
                {sectionFilter !== "All" && <span className="ml-1.5 text-indigo-400">· {sectionFilter}</span>}
              </p>
            </div>
            <button onClick={() => setMode(null)} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Exit
            </button>
          </div>
          <QuizMode questions={filteredQuestions} onExit={() => setMode(null)} trans={trans} />
        </div>
      </div>
    );
  }

  if (mode === "practice" && filteredQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-[#0a0d14] text-white px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-lg font-bold text-white">{topicName} — Practice</h2>
              <p className="text-xs text-slate-500">
                {filteredQuestions.length} questions · {examLabel}
                {sectionFilter !== "All" && <span className="ml-1.5 text-indigo-400">· {sectionFilter}</span>}
              </p>
            </div>
            <button onClick={() => setMode(null)} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
          <PracticeMode questions={filteredQuestions} trans={trans} />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={`${topicName} MCQ, ${topicName} questions ${examLabel}, ${subjectLabel} ${examLabel} questions, ${topicName} ${examLabel} practice, EMRS ${examLabel} ${topicName}, KVS ${examLabel} ${topicName}, NVS ${examLabel} ${topicName}, ${subjectLabel} MCQ for ${examLabel}, ${topicName} previous year questions, Descriptive Test`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDesc} />
        <link rel="canonical" href={`https://descriptivetest.com/question-bank/${examType}/${subject}/${topicSlug}`} />
        {faqSchema.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqSchema })}</script>
        )}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Question Bank", item: `${window.location.origin}/question-bank` },
            { "@type": "ListItem", position: 2, name: examLabel, item: `${window.location.origin}/question-bank/${examType}` },
            { "@type": "ListItem", position: 3, name: subjectLabel, item: `${window.location.origin}/question-bank/${examType}/${subject}` },
            { "@type": "ListItem", position: 4, name: `${topicName} MCQ`, item: `${window.location.origin}/question-bank/${examType}/${subject}/${topicSlug}` }
          ]
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0d14] text-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0a0d14] via-indigo-950/30 to-[#0a0d14] border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 py-10">
            {/* Breadcrumb with Home button */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-5 flex-wrap">
              <Link to="/dashboard" data-testid="home-btn" className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg transition-colors text-slate-300 hover:text-white font-medium text-xs">
                <Home className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/question-bank" className="hover:text-white">Question Bank</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/question-bank/${examType}`} className="hover:text-white">{examLabel}</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/question-bank/${examType}/${subject}`} className="hover:text-white">{subjectLabel}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300">{topicName}</span>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span className="bg-indigo-500/20 text-indigo-300 text-xs font-bold px-3 py-1 rounded-full border border-indigo-500/30">{examLabel}</span>
              <span className="bg-white/10 text-slate-300 text-xs px-3 py-1 rounded-full border border-white/10 capitalize">{subjectLabel}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-3">
              {topicName} MCQ Questions
            </h1>
            <p className="text-slate-400 text-sm mb-5">
              <span className="text-indigo-400 font-bold">{totalQ}</span> objective questions with AI-powered explanations for {examLabel} preparation.
              Free on <span className="text-indigo-400">Descriptive Test</span>.
            </p>

            {/* Section Tabs — only when multiple sections exist */}
            {availableSections.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Filter className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs text-slate-500 font-medium">Filter by Section</span>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="section-tabs">
                  {availableSections.map(s => {
                    const sectionColors = {
                      "Subject Knowledge": "bg-indigo-500/20 border-indigo-500/40 text-indigo-300",
                      "CDP": "bg-purple-500/20 border-purple-500/40 text-purple-300",
                      "General Awareness": "bg-emerald-500/20 border-emerald-500/40 text-emerald-300",
                      "Reasoning": "bg-orange-500/20 border-orange-500/40 text-orange-300",
                      "Language": "bg-pink-500/20 border-pink-500/40 text-pink-300",
                      "All": "bg-white/10 border-white/20 text-white",
                    };
                    const isActive = sectionFilter === s;
                    return (
                      <button
                        key={s}
                        data-testid={`section-tab-${s.toLowerCase().replace(/\s/g, "-")}`}
                        onClick={() => setSectionFilter(s)}
                        className={`text-xs px-3.5 py-1.5 rounded-full border font-semibold transition-all ${
                          isActive
                            ? (sectionColors[s] || "bg-indigo-600 border-indigo-500 text-white")
                            : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                        }`}
                      >
                        {s}
                        <span className={`ml-1.5 text-[10px] ${isActive ? "opacity-90" : "opacity-50"}`}>
                          ({sectionCountMap[s] || 0})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Mode Selection */}
            {!loading && filteredQuestions.length > 0 && (
              <div className="flex gap-3 flex-wrap">
                <button
                  data-testid="start-quiz-btn"
                  onClick={() => setMode("quiz")}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  <Zap className="w-4 h-4" /> Start Quiz Mode
                  {sectionFilter !== "All" && <span className="text-indigo-200 text-xs ml-1">({filteredQuestions.length}Q)</span>}
                </button>
                <button
                  data-testid="start-practice-btn"
                  onClick={() => setMode("practice")}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-xl border border-white/10 hover:border-white/20 transition-all"
                >
                  <BookOpen className="w-4 h-4" /> Practice Mode
                  {sectionFilter !== "All" && <span className="text-slate-400 text-xs ml-1">({filteredQuestions.length}Q)</span>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Questions + Sidebar */}
        <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8 items-start">
          <div className="flex-1 min-w-0">
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}
            </div>
          ) : !data || totalQ === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-lg mb-2">No questions yet for this topic.</p>
              <p className="text-slate-500 text-sm">Admin can generate questions from LMS Manager → Question Bank.</p>
              <Link to={`/question-bank/${examType}/${subject}`} className="text-indigo-400 hover:underline mt-4 inline-block">← Back to {subjectLabel}</Link>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400 mb-2">No questions in this section yet.</p>
              <button onClick={() => setSectionFilter("All")} className="text-indigo-400 hover:underline text-sm">Show all sections</button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Question Preview</h2>
                {sectionFilter !== "All" && (
                  <span className="text-xs text-slate-500">{filteredQuestions.length} questions in "{sectionFilter}"</span>
                )}
              </div>
              <div className="space-y-3">
                {filteredQuestions.slice(0, 5).map((q, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    {/* Section badge + paper label */}
                    {(q.section || q.paper_label) && (
                      <div className="flex gap-1.5 mb-2 flex-wrap">
                        {q.paper_label && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-500/20 border border-amber-500/40 text-amber-300">
                            {q.paper_label}
                          </span>
                        )}
                        {q.section && (
                          <span className={{
                            "Subject Knowledge": "text-[10px] px-2 py-0.5 rounded-full font-semibold bg-indigo-500/15 border border-indigo-500/30 text-indigo-400",
                            "CDP": "text-[10px] px-2 py-0.5 rounded-full font-semibold bg-purple-500/15 border border-purple-500/30 text-purple-400",
                            "General Awareness": "text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-500/15 border border-emerald-500/30 text-emerald-400",
                            "Reasoning": "text-[10px] px-2 py-0.5 rounded-full font-semibold bg-orange-500/15 border border-orange-500/30 text-orange-400",
                            "Language": "text-[10px] px-2 py-0.5 rounded-full font-semibold bg-pink-500/15 border border-pink-500/30 text-pink-400",
                          }[q.section] || "text-[10px] px-2 py-0.5 rounded-full font-semibold bg-white/10 border border-white/10 text-slate-400"}>
                            {q.section}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex gap-3">
                      <span className="w-6 h-6 bg-indigo-500/20 text-indigo-400 rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                      <p className="text-slate-300 text-sm leading-relaxed">{q.question_text}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 mt-2 ml-9">
                      {Object.entries(q.options || {}).map(([k, v]) => (
                        <span key={k} className="text-xs text-slate-500 truncate">{k}. {v}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {filteredQuestions.length > 5 && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setMode("practice")}
                    className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center gap-1.5 mx-auto"
                  >
                    View all {filteredQuestions.length} questions in Practice Mode <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* SEO text */}
          <div className="mt-8 pt-8 border-t border-white/5">
            <h2 className="text-lg font-bold text-white mb-3">About {topicName} MCQ for {examLabel}</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              These {topicName} multiple choice questions are carefully curated for {examLabel} exam preparation.
              Each MCQ includes a detailed AI-powered explanation to help understand the concept deeply.
              Regular practice of these {subjectLabel} questions will improve your performance in
              {examLabel}, DSSSB TGT, KVS, NVS, CTET, and other teaching recruitment exams.
            </p>
            <p className="text-slate-500 text-xs mt-3">
              All questions are free on <span className="text-indigo-400">Descriptive Test</span> — India's comprehensive platform for TGT/PGT preparation.
            </p>
          </div>
          </div>

          {/* Promo Sidebar — hidden for paid users */}
          {!isPaidUser && <PromoSidebar />}
        </div>

        <div className="border-t border-white/5 py-6 text-center">
          <p className="text-slate-600 text-xs">© 2026 <Link to="/" className="text-indigo-500">Descriptive Test</Link> — India's Best TGT/PGT Preparation Platform</p>
        </div>
      </div>

      {/* Paywall popup for free users hitting 3rd topic */}
      {showPaywall && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f1420] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <h2 className="text-xl font-black mb-1">You've used your 2 free topics</h2>
              <p className="text-indigo-200 text-sm">Upgrade to access all topics in the Question Bank</p>
            </div>
            <div className="p-6">
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4 mb-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-black text-indigo-300">₹499</span>
                  <span className="text-slate-500 text-sm">one-time</span>
                </div>
                <p className="text-slate-400 text-xs">Full access to all topics · Unlimited MCQs · Expert explanations</p>
              </div>
              <div className="space-y-2 mb-5">
                {["All question bank topics unlocked", "5000+ MCQ questions", "Previous year papers", "English + Hindi bilingual"].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
              <Link to="/pro" className="block w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-3 rounded-xl text-center text-sm transition-all mb-3" data-testid="paywall-upgrade-btn">
                Upgrade Now → ₹499
              </Link>
              <button onClick={() => setShowPaywall(false)} className="w-full text-slate-500 hover:text-slate-300 text-sm py-2 transition-colors" data-testid="paywall-close-btn">
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
